import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/globals.css';
import { App } from './App.js';
import { beginLogin, completeLogin, resumeSession, type AuthConfig } from './lib/auth.js';

/**
 * **入口**（M16）。
 *
 * ## 順序が体験を決める
 *
 * 1. **戻ってきた `?code=` があれば交換する**（Hosted UI からの復帰）
 * 2. **無ければ保存してある refresh で取り直す**（**D12 の「中断して戻れる」**。
 *    **30 日以内なら Hosted UI を再び見ない**）
 * 3. **どちらも駄目なら Hosted UI へ飛ばす**
 *
 * **2 を飛ばして毎回 3 へ行くと、戻ってきた学習者が毎回ログインさせられる。**
 */

/**
 * **設定が無いまま起動しない**（M16）。
 *
 * `VITE_*` は**ビルド時に埋め込まれる**ので、
 * **環境変数を渡し忘れたビルドは空文字のまま配布される**。
 * そのまま動かすと **`https:///oauth2/authorize` へ飛ばそうとして
 * 何も起きない**という、**最も原因が分かりにくい壊れ方**になる。
 *
 * **落とす**（INV-8 で単価表の版名を起動時に落としたのと同じ規律 —
 * **対話が終わってから分かるより、最初に落ちる方が安い**）。
 */
function requireEnv(name: 'VITE_COGNITO_DOMAIN' | 'VITE_COGNITO_CLIENT_ID'): string {
  const v = import.meta.env[name];
  if (v === undefined || v === '') {
    throw new Error(`${name} が設定されていないビルドです（ビルド時に渡す必要があります）`);
  }
  return v;
}

const cfg: AuthConfig = {
  domain: requireEnv('VITE_COGNITO_DOMAIN'),
  clientId: requireEnv('VITE_COGNITO_CLIENT_ID'),
  redirectUri: location.origin + '/',
};

async function boot(): Promise<void> {
  const root = createRoot(document.getElementById('root') as HTMLElement);

  const tokens = (await completeLogin(cfg)) ?? (await resumeSession(cfg));
  if (tokens === undefined) {
    await beginLogin(cfg);
    return;
  }

  root.render(
    <StrictMode>
      <App api={{ baseUrl: '', idToken: tokens.idToken }} />
    </StrictMode>,
  );
}

void boot().catch((e: unknown) => {
  /**
   * **白い画面を出さない。**
   * **設定漏れも通信断も、ここに落ちてくる。**
   * **中身は出す** — この段階で秘密は持っていない（トークン交換より前）。
   */
  const el = document.getElementById('root');
  if (el !== null) {
    el.textContent = `起動できませんでした: ${e instanceof Error ? e.message : String(e)}`;
  }
  // eslint-disable-next-line no-console
  console.error('[ua] boot failed', e);
});
